import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Button,
  Container,
  Grid,
  Box
} from '@mui/material';

import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';

// Theme Stuff / Theme Configs
import { getMuiTableBodyProps, getTableTheme } from '../../js/table_theme_config'; // Import the helpers

// Pure Local JS Imports
import downloadFile, { getFileSize } from '../../js/download_file'
import query_data from '../../js/query_data';
import { convertEpochToDateTime } from '../../js/time_helpers';

// Component Imports
import LogsFilterDialog from './LogsFilterDialog';
import CreateDatasetDialog from '../dataset/CreateDatasetDialog';
import { useSearchParams } from 'react-router';
import GenericFolderUpload from '../../components/upload/GenericFolderUpload';
import { DownloadModalBase, ProgressSnackbarBase, FileSizeEntry } from '../common/DownloadModalBase';

export type FileSizeMap = Record<string, FileSizeEntry>;

const LogsTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [rowCount, setRowCount] = useState(0);

  // For Table State
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const [isCreateDatasetDialogOpen, setIsCreateDatasetDialogOpen] = useState<boolean>(false);
  const [downloadModal, setDownloadModal] = useState<boolean>(false);
  const [fileSizes, setFileSizes] = useState<FileSizeMap>({});
  const [progressToast, setProgressToast] = useState<boolean>(false);


  // Logs Filter Dialog State Vars
  const [isLogsFilterDialogOpen, setIsLogsFilterDialogOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams({
    compute_type: [],
    analytics: [],
    launch_file_name: [],
    log_number: [],
    robot: [],
    features: [],
    run_id: [],
    is_sim: 'false',
    is_onboard: 'false',
    start_time_ns: null,
    end_time_ns: null,
  });

  const process_data = async (data_list) => {
    // This groups all the common run_id into a dict {run_id: [list of logs]}
    // also it updates the element start_time to a human readable format
    var grouped_data = {};
    for (const element of data_list) {
      if (grouped_data.hasOwnProperty(element['run_id'])) {
        grouped_data[element['run_id']].push(
          {
            ...element,
            start_time: convertEpochToDateTime(element.start_time_epoch_ns),
          }
        );
      } else {
        grouped_data[element['run_id']] = [
          {
            ...element,
            start_time: convertEpochToDateTime(element.start_time_epoch_ns),
          }
        ];
      }
    }
    // This converts from the dict {run_id: [list of logs]} to a list of logs {main_row_log, subrows: [list of logs]}
    // Note -> Main Row Log is just the first log seen during the previous grouping (it is not based on anything else)
    const processed_data = Object.keys(grouped_data).map((run_id) => ({
      ...grouped_data[run_id][0],
      filename: "",
      /*
        Explanation: bucket_name_file_path is the unique identifier for each row in the table.
        We override it here because its the main row (and not a subrow) if we left it the same there would be two rows that 
        have the same bucket_name_file_path which would cause issues with the table.
        I override it to run_id since thats unique across all group of logs (and its the main row)
      */
      bucket_name_file_path: `GROUPING_ROW-${run_id}`,
      subRows: grouped_data[run_id],
    }));

    return processed_data;
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const jsonParams = convertParamsToJSON(searchParams);
      const queryParams = {
        ...jsonParams,
        page: pagination.pageIndex + 1, // API is 1-based
        size: pagination.pageSize,
      };
      const json_list = await query_data("/logs", queryParams);
      const processedData = await process_data(json_list.data);
      setData(processedData);
      setRowCount(json_list.total);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }, [pagination, searchParams]);


  const handleDownloadSelected = async (bucket_name_file_paths: string[]) => {
    console.log("Selected logs - Minio Bucket File Paths:", bucket_name_file_paths);
    setProgressToast(true);

    const downloaders = bucket_name_file_paths.map(async (bucket_name_file_path) => {
      setFileSizes(prevSizes => ({
        ...prevSizes,
        [bucket_name_file_path]: {
          ...prevSizes[bucket_name_file_path],
          size: 0, // Reset size to 0 before download
          isDownloading: true,
        },
      }));
      // Download the file
      try {
        await downloadFile(bucket_name_file_path, (progress) => {
          // Update the file size and download progress in the state
          setFileSizes(prevSizes => ({
            ...prevSizes,
            [bucket_name_file_path]: {
              ...prevSizes[bucket_name_file_path],
              downloadProgress: progress,
            },
          }));
        });

        // Set isDownloading to false
        setFileSizes(prevSizes => ({
          ...prevSizes,
          [bucket_name_file_path]: {
            ...prevSizes[bucket_name_file_path],
            isDownloading: false,
            size: prevSizes[bucket_name_file_path].size, // Update with actual size if needed
          },
        }));
        console.log("Download complete!");
      } catch (err) {

        setFileSizes(prevSizes => ({
          ...prevSizes,
          [bucket_name_file_path]: {
            ...prevSizes[bucket_name_file_path],
            isDownloading: false,
            error: "Download failed.",
          },
        }));
        console.error("Download error:", err);
      }
    });
    Promise.all(downloaders)
      .then(() => {
        console.log("All downloads completed successfully.");
        setProgressToast(false);
      }
      )
      .catch((err) => {
        console.error("Error during downloads:", err);
        setProgressToast(false);
      }
      );
  };


  const onSaveCallback = (requestBody) => {
    setSearchParams(requestBody);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
    setIsLogsFilterDialogOpen(false);
  };


  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const columns = useMemo(
    () => {
      return [
        {
          accessorKey: 'start_time',
          header: 'Start Time',
          size: 120,
          Cell: ({ cell }) => (
            <div>
              <div>{cell.getValue().date}</div>
              <div>{cell.getValue().time}</div>
            </div>
          ),
        },
        {
          accessorKey: 'robot',
          header: 'Robot',
          size: 120,
        },
        {
          accessorKey: 'run_id',
          header: 'Run Id',
          size: 200,
        },
        {
          accessorKey: 'launch_file_name',
          header: 'Launch File Name',
          size: 120,
        },
        {
          accessorKey: 'filename',
          header: 'File Name',
          size: 150,
        },
      ]
    },
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data,
    enableRowSelection: true,
    enableStickyHeader: true,
    enableExpanding: true,
    filterFromLeafRows: true,
    getRowId: (row) => row.bucket_name_file_path,
    manualPagination: true,
    rowCount,
    onPaginationChange: setPagination,
    state: { isLoading: loading, pagination },
    muiTableBodyProps: {
      ...getMuiTableBodyProps(false),
      sx: {
        ...((getMuiTableBodyProps(false) && getMuiTableBodyProps(false).sx) || {}),
        '& tr': { backgroundColor: 'inherit' },
        '& td': { padding: '0.5rem' },
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: 'calc(100vh - 300px)',
        overflowY: 'auto',
      },
    },
    mrtTheme: getTableTheme(false), // Use the common props (Darkmode -> true)
    positionToolbarAlertBanner: 'bottom',
    renderToolbarAlertBannerContent: ({ table }) => {
      const selectedFilesCount = Object.keys(table.getState().rowSelection).filter(
        (path) => !path.startsWith('GROUPING_ROW')
      ).length;

      const totalFilesOnPage = table.getPaginationRowModel().rows.reduce(
        (acc, row) => acc + (row.original.subRows?.length || 0),
        0
      );

      return (
        <Box sx={{ p: '0 1rem' }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              {`${selectedFilesCount} of ${totalFilesOnPage} file(s) selected`}
            </Grid>
            <Grid item>
              <Button
                color="primary"
                onClick={() => table.resetRowSelection()}
                variant="text"
              >
                Clear Selection
              </Button>
            </Grid>
          </Grid>
        </Box>
      );
    },
    muiPaginationProps: {
      rowsPerPageOptions: [10, 20, 50, 100],
    },
    renderTopToolbarCustomActions: () => (
      <Grid container justifyContent="flex-start">
        <Button onClick={fetchData}>Refresh</Button>
        <Button onClick={() => setIsLogsFilterDialogOpen(true)}>Set Filters</Button>
        <GenericFolderUpload data_type="ROBOT_LOG" buttonText="Upload Logs" />
        {Object.keys(table.getState().rowSelection).length > 0 && (
          <>
            <Button onClick={() => {
              setDownloadModal(true);

              Object.keys(table.getState().rowSelection).forEach(async (bucket_name_file_path) => {
                if (bucket_name_file_path.startsWith('GROUPING_ROW')) return
                await getFileSize(bucket_name_file_path).then((res) => {
                  setFileSizes(prevSizes => ({
                    ...prevSizes,
                    [bucket_name_file_path]: {
                      size: res,
                      downloadProgress: 0,
                      isDownloading: false,
                      error: null,
                    },
                  }));
                }).catch((err) => {
                  console.error("Error getting file size:", err);
                });
              });
            }}>
              Download Logs
            </Button>
            <Button onClick={() => setIsCreateDatasetDialogOpen(true)}> Create Dataset </Button>
          </>
        )
        }
      </Grid>
    )
  });

  const selected = Object.keys(table.getState().rowSelection).filter(path => !path.startsWith('GROUPING_ROW'))

  return (
    <Container maxWidth={false} disableGutters sx={{ pt: 2, width: '100%' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={8} container spacing={2} alignItems="center">
          <LogsFilterDialog
            open={isLogsFilterDialogOpen}
            onClose={() => setIsLogsFilterDialogOpen(false)}
            onSaveCallback={requestBody => onSaveCallback(requestBody)}
            searchParams={searchParams}
          />
          <CreateDatasetDialog
            open={isCreateDatasetDialogOpen}
            onClose={() => setIsCreateDatasetDialogOpen(false)}
            minioBucketFilePaths={selected}
          />
        </Grid>
      </Grid>
      <MaterialReactTable table={table} />
      <DownloadModalBase
        open={downloadModal}
        setOpen={setDownloadModal}
        selected={selected}
        fileSizes={fileSizes}
        setFileSizes={setFileSizes}
        handleDownloadSelected={handleDownloadSelected}
        getKey={key => key}
        getName={key => key.split('/').pop() || key}
        title="Download Selected Logs"
        itemLabel="Logs"
      />

      <ProgressSnackbarBase
        open={progressToast}
        setOpen={setProgressToast}
        selected={selected}
        fileSizes={fileSizes}
        setFileSizes={setFileSizes}
        getKey={key => key}
        getName={key => key.split('/').pop() || key}
        headline="Downloading Logs"
      />
    </Container >
  );
};

export default LogsTable;

function convertParamsToJSON(params: URLSearchParams) {
  const startTimeStr = params.get('start_time_ns');
  const endTimeStr = params.get('end_time_ns');

  return {
    compute_type: params.getAll('compute_type').map((val) => Number(val)),
    analytics: params.getAll('analytics'),
    launch_file_name: params.getAll('launch_file_name'),
    log_number: params.getAll('log_number').map((val) => Number(val)),
    robot: params.getAll('robot'),
    run_id: params.getAll('run_id'),
    features: params.getAll('features').map((val) => Number(val)),
    start_time_ns: (startTimeStr && startTimeStr !== 'null') ? startTimeStr : null,
    end_time_ns: (endTimeStr && endTimeStr !== 'null') ? endTimeStr : null,
    is_onboard: params.get('is_onboard') === 'true',
    is_sim: params.get('is_sim') === 'true',
  }
}
